import { Component } from '../lib/ComponentBase';
import { registerComponent } from '../lib/ComponentHelpers';
import type { ProposalProvider, DisplayProvider, DisplayInfo } from '../types/Components';
import type Action from '../lib/Action';
import type { Proposal } from '../lib/Proposal';
import { ActionType } from '../lib/Action';
import { ProposalType } from '../lib/Proposal';

/**
 * An ability that lets an actor attack a single target by moving forward.
 * It replaces a MOVE action with an ATTACK, and moves on a miss.
 */
class ForwardStab extends Component implements ProposalProvider, DisplayProvider {
  damage: number;
  priority: number;

  constructor(data: { damage?: number, priority?: number } = {}) {
    super();
    this.damage = data.damage ?? 10;
    this.priority = data.priority ?? 50;
  }

  /**
   * Generates a proposal to replace a MOVE action with an ATTACK.
   */
  getProposedOutcome(selfAction: Action): Proposal | null {
    if (selfAction.type !== ActionType.MOVE || !selfAction.details.coords) {
      return null;
    }

    const destination = selfAction.details.coords;

    const effect = {
      type: 'ATTACK' as const,
      attackHex: destination,
      damage: this.damage,
      attackTiming: 'CONFLICT' as const,
      animationPlan: [[{
        type: 'ATTACK_JAB',
        entityId: selfAction.entity.id,
        targetCoords: destination,
      }]],
      onBlocked: {
        animationPlan: [[{
          type: 'ATTACK_JAB', // Play the same "miss" animation when blocked
          entityId: selfAction.entity.id,
          targetCoords: destination,
        }]],
      },
    };

    return {
      type: ProposalType.REPLACEMENT,
      priority: this.priority,
      effects: [effect],
    };
  }

  getDisplayInfo(): DisplayInfo {
    return { icon: 'game-icons:plain-dagger', label: 'Forward Stab', description: 'Stabs the hex directly ahead.' };
  }

  equals(other: this): boolean {
    return this.damage === other.damage &&
           this.priority === other.priority;
  }

  static getTestInstances(): ForwardStab[] {
    return [
      new ForwardStab({ damage: 10, priority: 50 }),
      new ForwardStab({ damage: 20, priority: 60 }),
      new ForwardStab({ damage: 5, priority: 40 }),
    ];
  }
}

export default registerComponent(ForwardStab);
